import { type CSSProperties, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "taskguard-ui/button";
import { EmptyState } from "taskguard-ui/empty-state";
import { InfoChip } from "taskguard-ui/info-chip";
import { AppPanel, BoardBadges, BoardCopy, BoardHeader, BoardPanel } from "taskguard-ui/surfaces";
import { CopyText, Eyebrow } from "taskguard-ui/text";
import { TODO_SCHEMA_VERSION, type TodoRecord, toVisibleTodos } from "../../shared/todos";
import { formatDateTime, formatVaultId } from "../../format";
import { useTodoStore } from "../../store";

type CalendarScope = "day" | "week" | "month" | "year";

const DAY_MS = 24 * 60 * 60 * 1000;
const DEFAULT_SCOPE: CalendarScope = "month";
const SCOPE_OPTIONS: CalendarScope[] = ["day", "week", "month", "year"];
const DEFAULT_BORDER_COLOR = "rgba(36, 38, 43, 0.08)";
const MINI_BORDER_COLOR = "rgba(36, 38, 43, 0.06)";
const MINI_DAY_BACKGROUND = "rgba(247, 244, 239, 0.72)";

const weekdayShortFormatter = new Intl.DateTimeFormat(undefined, { weekday: "short" });
const weekdayLongFormatter = new Intl.DateTimeFormat(undefined, { weekday: "long" });
const weekdayNarrowFormatter = new Intl.DateTimeFormat(undefined, { weekday: "narrow" });
const monthYearFormatter = new Intl.DateTimeFormat(undefined, { month: "long", year: "numeric" });
const monthShortFormatter = new Intl.DateTimeFormat(undefined, { month: "short" });
const dateHeaderFormatter = new Intl.DateTimeFormat(undefined, {
  weekday: "long",
  month: "long",
  day: "numeric",
  year: "numeric",
});
const monthDayFormatter = new Intl.DateTimeFormat(undefined, {
  month: "short",
  day: "numeric",
});
const timeFormatter = new Intl.DateTimeFormat(undefined, {
  hour: "numeric",
  minute: "2-digit",
});
const yearFormatter = new Intl.DateTimeFormat(undefined, { year: "numeric" });

const weekdayAnchors = Array.from({ length: 7 }, (_, index) => new Date(2024, 0, 7 + index));
const weekdayLabels = weekdayAnchors.map((date) => weekdayShortFormatter.format(date));

const toolbarStyle: CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  justifyContent: "space-between",
  gap: "0.85rem",
  alignItems: "center",
  marginTop: "1rem",
};

const toolbarGroupStyle: CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: "0.6rem",
  alignItems: "center",
};

const monthShellStyle: CSSProperties = {
  marginTop: "1rem",
  overflowX: "auto",
  paddingBottom: "0.2rem",
};

const monthGridStyle: CSSProperties = {
  minWidth: "980px",
  display: "grid",
  gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
  gap: "0.75rem",
};

const monthCellBaseStyle: CSSProperties = {
  minHeight: "180px",
  display: "grid",
  alignContent: "start",
  gap: "0.45rem",
  padding: "0.8rem",
  borderRadius: "22px",
  border: `1px solid ${DEFAULT_BORDER_COLOR}`,
  background: "rgba(255, 255, 255, 0.72)",
};

const monthCellMutedStyle: CSSProperties = {
  background: "rgba(247, 244, 239, 0.85)",
  color: "rgba(107, 111, 121, 0.92)",
};

const monthDayHeaderStyle: CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "0.5rem",
};

const monthTaskPreviewStyle: CSSProperties = {
  padding: "0.5rem 0.55rem",
  borderRadius: "16px",
  border: "1px solid rgba(36, 38, 43, 0.06)",
  background: "rgba(255, 255, 255, 0.9)",
  display: "grid",
  gap: "0.2rem",
};

const weekGridStyle: CSSProperties = {
  marginTop: "1rem",
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: "0.85rem",
};

const dayStackStyle: CSSProperties = {
  marginTop: "1rem",
  display: "grid",
  gap: "0.85rem",
};

const taskListStyle: CSSProperties = {
  display: "grid",
  gap: "0.65rem",
  marginTop: "0.85rem",
};

const taskCardStyle: CSSProperties = {
  display: "grid",
  gap: "0.35rem",
  padding: "0.8rem 0.9rem",
  borderRadius: "18px",
  border: "1px solid rgba(36, 38, 43, 0.08)",
  background: "rgba(255, 255, 255, 0.82)",
};

const taskCardHeaderStyle: CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  gap: "0.7rem",
  alignItems: "baseline",
};

const yearGridStyle: CSSProperties = {
  marginTop: "1rem",
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
  gap: "0.85rem",
};

const miniMonthLabelRowStyle: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
  gap: "0.35rem",
  marginTop: "0.75rem",
};

const miniMonthGridStyle: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
  gap: "0.35rem",
  marginTop: "0.45rem",
};

const miniMonthDayStyle: CSSProperties = {
  minHeight: "2.1rem",
  borderRadius: "12px",
  border: `1px solid ${MINI_BORDER_COLOR}`,
  background: MINI_DAY_BACKGROUND,
  display: "grid",
  placeItems: "center",
  position: "relative",
  fontSize: "0.8rem",
};

const miniMonthDotStyle: CSSProperties = {
  width: "0.35rem",
  height: "0.35rem",
  borderRadius: "999px",
  background: "rgba(223, 78, 61, 0.92)",
  position: "absolute",
  bottom: "0.3rem",
};

function isCalendarScope(value: string | null): value is CalendarScope {
  return value === "day" || value === "week" || value === "month" || value === "year";
}

function startOfDay(value: number | Date) {
  const date = new Date(value);
  date.setHours(0, 0, 0, 0);
  return date.getTime();
}

function startOfWeek(value: number | Date) {
  const date = new Date(startOfDay(value));
  date.setDate(date.getDate() - date.getDay());
  return date.getTime();
}

function startOfMonth(value: number | Date) {
  const date = new Date(startOfDay(value));
  date.setDate(1);
  return date.getTime();
}

function startOfYear(value: number | Date) {
  const date = new Date(startOfDay(value));
  date.setMonth(0, 1);
  return date.getTime();
}

function addDays(value: number, amount: number) {
  return startOfDay(value + amount * DAY_MS);
}

function addMonthsSafe(value: number, amount: number) {
  const date = new Date(startOfDay(value));
  const targetDay = date.getDate();
  const nextDate = new Date(date.getFullYear(), date.getMonth() + amount, 1);
  const lastDay = new Date(nextDate.getFullYear(), nextDate.getMonth() + 1, 0).getDate();
  nextDate.setDate(Math.min(targetDay, lastDay));
  return startOfDay(nextDate);
}

function addYearsSafe(value: number, amount: number) {
  const date = new Date(startOfDay(value));
  const nextDate = new Date(date.getFullYear() + amount, date.getMonth(), 1);
  const lastDay = new Date(nextDate.getFullYear(), nextDate.getMonth() + 1, 0).getDate();
  nextDate.setDate(Math.min(date.getDate(), lastDay));
  return startOfDay(nextDate);
}

function getScopeRange(anchor: number, scope: CalendarScope) {
  if (scope === "day") {
    const start = startOfDay(anchor);
    return { start, end: addDays(start, 1) };
  }

  if (scope === "week") {
    const start = startOfWeek(anchor);
    return { start, end: addDays(start, 7) };
  }

  if (scope === "month") {
    const start = startOfMonth(anchor);
    return { start, end: startOfMonth(addMonthsSafe(start, 1)) };
  }

  const start = startOfYear(anchor);
  return { start, end: startOfYear(addYearsSafe(start, 1)) };
}

function moveAnchor(anchor: number, scope: CalendarScope, delta: number) {
  if (scope === "day") {
    return addDays(anchor, delta);
  }

  if (scope === "week") {
    return addDays(anchor, delta * 7);
  }

  if (scope === "month") {
    return addMonthsSafe(anchor, delta);
  }

  return addYearsSafe(anchor, delta);
}

function formatDateParam(value: number) {
  const date = new Date(startOfDay(value));
  const pad = (part: number) => part.toString().padStart(2, "0");
  return [date.getFullYear(), pad(date.getMonth() + 1), pad(date.getDate())].join("-");
}

function parseDateParam(value: string | null) {
  if (value == null) {
    return null;
  }

  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);

  if (match == null) {
    return null;
  }

  const [, yearValue, monthValue, dayValue] = match;
  const parsed = new Date(Number(yearValue), Number(monthValue) - 1, Number(dayValue));
  const timestamp = parsed.getTime();

  return Number.isNaN(timestamp) ? null : startOfDay(timestamp);
}

function formatPeriodLabel(anchor: number, scope: CalendarScope) {
  if (scope === "day") {
    return dateHeaderFormatter.format(anchor);
  }

  if (scope === "week") {
    const start = startOfWeek(anchor);
    const end = addDays(start, 6);
    return `${monthDayFormatter.format(start)} - ${dateHeaderFormatter.format(end)}`;
  }

  if (scope === "month") {
    return monthYearFormatter.format(anchor);
  }

  return yearFormatter.format(anchor);
}

function sortByDueAt(left: TodoRecord, right: TodoRecord) {
  return (
    (left.dueAt ?? Number.POSITIVE_INFINITY) - (right.dueAt ?? Number.POSITIVE_INFINITY) ||
    Number(left.completed) - Number(right.completed) ||
    right.updatedAt - left.updatedAt
  );
}

function buildDueBuckets(todos: TodoRecord[]) {
  const buckets = new Map<number, TodoRecord[]>();

  for (const todo of todos) {
    if (todo.dueAt == null) {
      continue;
    }

    const dayKey = startOfDay(todo.dueAt);
    const bucket = buckets.get(dayKey);

    if (bucket == null) {
      buckets.set(dayKey, [todo]);
      continue;
    }

    bucket.push(todo);
  }

  for (const bucket of buckets.values()) {
    bucket.sort(sortByDueAt);
  }

  return buckets;
}

function getMonthGrid(anchor: number) {
  const monthStart = startOfMonth(anchor);
  const visibleStart = startOfWeek(monthStart);
  return Array.from({ length: 42 }, (_, index) => addDays(visibleStart, index));
}

function getMonthPreviewTasks(todos: TodoRecord[]) {
  return todos.slice(0, 3);
}

function getMonthCardDays(anchor: number) {
  const monthStart = startOfMonth(anchor);
  const visibleStart = startOfWeek(monthStart);
  const monthEnd = addDays(startOfMonth(addMonthsSafe(monthStart, 1)), -1);
  const visibleEnd = addDays(startOfWeek(monthEnd), 6);
  const totalDays = Math.round((visibleEnd - visibleStart) / DAY_MS) + 1;

  return Array.from({ length: totalDays }, (_, index) => addDays(visibleStart, index));
}

function CalendarTaskCard({ todo, compact = false }: { todo: TodoRecord; compact?: boolean }) {
  return (
    <div
      style={{
        ...taskCardStyle,
        padding: compact ? "0.55rem 0.6rem" : taskCardStyle.padding,
        opacity: todo.completed ? 0.72 : 1,
      }}
    >
      <div style={taskCardHeaderStyle}>
        <strong
          style={{
            textDecoration: todo.completed ? "line-through" : "none",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {todo.text}
        </strong>
        <CopyText as="span">{todo.dueAt == null ? "No time" : timeFormatter.format(todo.dueAt)}</CopyText>
      </div>
      {!compact ? (
        <CopyText as="div">
          {todo.completed ? "Completed" : "Open"} and updated {formatDateTime(todo.updatedAt)}
        </CopyText>
      ) : null}
    </div>
  );
}

export function CalendarPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeVault = useTodoStore((state) => (state.activeVaultId == null ? null : (state.vaults[state.activeVaultId] ?? null)));
  const todos = useMemo(
    () =>
      activeVault == null
        ? []
        : toVisibleTodos({
            schemaVersion: TODO_SCHEMA_VERSION,
            deviceId: "",
            lastMutatedAt: activeVault.lastMutatedAt,
            todos: activeVault.todos,
          }),
    [activeVault],
  );
  const scopeParam = searchParams.get("scope");
  const scope: CalendarScope = isCalendarScope(scopeParam) ? scopeParam : DEFAULT_SCOPE;
  const anchor = parseDateParam(searchParams.get("date")) ?? startOfDay(Date.now());
  const scheduledTodos = useMemo(() => todos.filter((todo) => todo.dueAt != null).sort(sortByDueAt), [todos]);
  const dueBuckets = useMemo(() => buildDueBuckets(scheduledTodos), [scheduledTodos]);
  const range = useMemo(() => getScopeRange(anchor, scope), [anchor, scope]);
  const tasksInScope = useMemo(
    () => scheduledTodos.filter((todo) => todo.dueAt != null && todo.dueAt >= range.start && todo.dueAt < range.end),
    [range.end, range.start, scheduledTodos],
  );
  const unscheduledCount = todos.length - scheduledTodos.length;
  const completedCount = todos.filter((todo) => todo.completed).length;
  const openCount = todos.length - completedCount;
  const today = startOfDay(Date.now());

  const updateView = (nextScope: CalendarScope, nextAnchor: number) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("scope", nextScope);
    nextParams.set("date", formatDateParam(nextAnchor));
    setSearchParams(nextParams);
  };

  const renderDayView = () => {
    const dayStart = startOfDay(anchor);
    const dayTodos = dueBuckets.get(dayStart) ?? [];

    return (
      <div style={dayStackStyle}>
        <AppPanel>
          <Eyebrow>{weekdayLongFormatter.format(dayStart)}</Eyebrow>
          <h3 style={{ marginTop: "0.2rem" }}>{dateHeaderFormatter.format(dayStart)}</h3>
          <CopyText>
            {dayTodos.length === 0
              ? "No tasks are due on this day."
              : `${dayTodos.length} task${dayTodos.length === 1 ? "" : "s"} due on this date.`}
          </CopyText>

          {dayTodos.length === 0 ? (
            <EmptyState title="Nothing due here.">
              Move to another date or add a due date from the task board to populate this day.
            </EmptyState>
          ) : (
            <div style={taskListStyle}>
              {dayTodos.map((todo) => (
                <CalendarTaskCard key={todo.id} todo={todo} />
              ))}
            </div>
          )}
        </AppPanel>
      </div>
    );
  };

  const renderWeekView = () => {
    const weekStart = startOfWeek(anchor);
    const days = Array.from({ length: 7 }, (_, index) => addDays(weekStart, index));

    return (
      <div style={weekGridStyle}>
        {days.map((dayStart) => {
          const dayTodos = dueBuckets.get(dayStart) ?? [];

          return (
            <AppPanel key={dayStart}>
              <Eyebrow>{weekdayShortFormatter.format(dayStart)}</Eyebrow>
              <h3 style={{ marginTop: "0.2rem" }}>{monthDayFormatter.format(dayStart)}</h3>
              <CopyText>
                {dayTodos.length === 0 ? "No due tasks." : `${dayTodos.length} due task${dayTodos.length === 1 ? "" : "s"}.`}
              </CopyText>

              {dayTodos.length > 0 ? (
                <div style={taskListStyle}>
                  {dayTodos.map((todo) => (
                    <CalendarTaskCard compact key={todo.id} todo={todo} />
                  ))}
                </div>
              ) : null}
            </AppPanel>
          );
        })}
      </div>
    );
  };

  const renderMonthView = () => {
    const days = getMonthGrid(anchor);
    const currentMonth = new Date(anchor).getMonth();
    const currentYear = new Date(anchor).getFullYear();

    return (
      <div style={monthShellStyle}>
        <div style={monthGridStyle}>
          {weekdayLabels.map((label) => (
            <CopyText as="div" key={label} style={{ padding: "0 0.35rem" }}>
              {label}
            </CopyText>
          ))}

          {days.map((dayStart) => {
            const date = new Date(dayStart);
            const isCurrentMonth = date.getMonth() === currentMonth && date.getFullYear() === currentYear;
            const dayTodos = dueBuckets.get(dayStart) ?? [];
            const previewTodos = getMonthPreviewTasks(dayTodos);
            const isToday = dayStart === today;

            return (
              <div
                key={dayStart}
                style={{
                  ...monthCellBaseStyle,
                  ...(isCurrentMonth ? null : monthCellMutedStyle),
                  borderColor: isToday ? "rgba(223, 78, 61, 0.28)" : DEFAULT_BORDER_COLOR,
                  boxShadow: isToday ? "0 12px 26px rgba(223, 78, 61, 0.08)" : "none",
                }}
              >
                <div style={monthDayHeaderStyle}>
                  <strong>{date.getDate()}</strong>
                  {dayTodos.length > 0 ? <InfoChip tone={isToday ? "active" : "default"}>{dayTodos.length} due</InfoChip> : null}
                </div>

                {previewTodos.map((todo) => (
                  <div
                    key={todo.id}
                    style={{
                      ...monthTaskPreviewStyle,
                      opacity: todo.completed ? 0.72 : 1,
                    }}
                  >
                    <strong
                      style={{
                        textDecoration: todo.completed ? "line-through" : "none",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {todo.text}
                    </strong>
                    <CopyText as="span">{todo.dueAt == null ? "No time" : timeFormatter.format(todo.dueAt)}</CopyText>
                  </div>
                ))}

                {dayTodos.length > previewTodos.length ? <CopyText>+{dayTodos.length - previewTodos.length} more</CopyText> : null}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderYearView = () => {
    const selectedYear = new Date(anchor).getFullYear();
    const monthStarts = Array.from({ length: 12 }, (_, index) => new Date(selectedYear, index, 1).getTime());

    return (
      <div style={yearGridStyle}>
        {monthStarts.map((monthStart) => {
          const days = getMonthCardDays(monthStart);
          const monthTasks = scheduledTodos.filter((todo) => {
            if (todo.dueAt == null) {
              return false;
            }

            const dueDate = new Date(todo.dueAt);
            return dueDate.getFullYear() === selectedYear && dueDate.getMonth() === new Date(monthStart).getMonth();
          });
          const previewTasks = monthTasks.slice(0, 2);

          return (
            <AppPanel key={monthStart}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: "0.75rem" }}>
                <div>
                  <Eyebrow>{yearFormatter.format(monthStart)}</Eyebrow>
                  <h3 style={{ marginTop: "0.2rem" }}>{monthYearFormatter.format(monthStart)}</h3>
                </div>
                <InfoChip tone={monthTasks.length > 0 ? "active" : "muted"}>{monthTasks.length} due</InfoChip>
              </div>

              <div style={miniMonthLabelRowStyle}>
                {weekdayAnchors.map((date) => (
                  <CopyText as="div" key={`${monthStart}-${weekdayShortFormatter.format(date)}`} style={{ textAlign: "center" }}>
                    {weekdayNarrowFormatter.format(date)}
                  </CopyText>
                ))}
              </div>

              <div style={miniMonthGridStyle}>
                {days.map((dayStart) => {
                  const isSameMonth = new Date(dayStart).getMonth() === new Date(monthStart).getMonth();
                  const dayTodos = dueBuckets.get(dayStart) ?? [];
                  const isToday = dayStart === today;

                  return (
                    <div
                      key={dayStart}
                      style={{
                        ...miniMonthDayStyle,
                        color: isSameMonth ? "inherit" : "rgba(107, 111, 121, 0.72)",
                        background: dayTodos.length > 0 ? "rgba(255, 241, 236, 0.92)" : MINI_DAY_BACKGROUND,
                        borderColor: isToday ? "rgba(223, 78, 61, 0.28)" : MINI_BORDER_COLOR,
                      }}
                    >
                      <span>{new Date(dayStart).getDate()}</span>
                      {dayTodos.length > 0 ? <span style={miniMonthDotStyle} /> : null}
                    </div>
                  );
                })}
              </div>

              {previewTasks.length > 0 ? (
                <div style={taskListStyle}>
                  {previewTasks.map((todo) => (
                    <CopyText key={todo.id}>
                      {todo.dueAt == null ? "" : `${monthShortFormatter.format(todo.dueAt)} ${new Date(todo.dueAt).getDate()}`}
                      {todo.dueAt == null ? "" : " "}
                      {todo.dueAt == null ? "" : timeFormatter.format(todo.dueAt)} {todo.text}
                    </CopyText>
                  ))}
                </div>
              ) : (
                <CopyText style={{ marginTop: "0.85rem" }}>No due tasks this month.</CopyText>
              )}
            </AppPanel>
          );
        })}
      </div>
    );
  };

  if (activeVault == null) {
    return (
      <BoardPanel empty>
        <Eyebrow>Calendar</Eyebrow>
        <h2>Pick a vault before opening the schedule.</h2>
        <EmptyState title="No active vault selected.">
          The calendar is driven by task due dates inside the currently active vault.
        </EmptyState>
        <Button block onClick={() => navigate("/vaults")} type="button">
          Open vaults
        </Button>
      </BoardPanel>
    );
  }

  if (scheduledTodos.length === 0) {
    return (
      <BoardPanel empty>
        <BoardHeader>
          <div>
            <Eyebrow>Calendar</Eyebrow>
            <h2>{activeVault.name}</h2>
            <BoardCopy>Due dates turn tasks into calendar entries. Add one from the task board and it will land here.</BoardCopy>
          </div>
          <BoardBadges>
            <InfoChip>Vault {formatVaultId(activeVault.id)}</InfoChip>
            <InfoChip>{openCount} open</InfoChip>
            <InfoChip tone="muted">{unscheduledCount} without due dates</InfoChip>
          </BoardBadges>
        </BoardHeader>

        <EmptyState title="No scheduled tasks yet.">
          Every task without a due date still stays in the vault, but the calendar only renders tasks that have a due date and time.
        </EmptyState>

        <Button onClick={() => navigate(`/vaults/${activeVault.id}`)} type="button">
          Open task board
        </Button>
      </BoardPanel>
    );
  }

  return (
    <BoardPanel>
      <BoardHeader>
        <div>
          <Eyebrow>Calendar</Eyebrow>
          <h2>{activeVault.name}</h2>
          <BoardCopy>
            View scheduled tasks by day, week, month, or year. Due dates sync with the rest of the vault and stay encrypted at rest in this
            browser.
          </BoardCopy>
        </div>
        <BoardBadges>
          <InfoChip>Vault {formatVaultId(activeVault.id)}</InfoChip>
          <InfoChip tone="active">{tasksInScope.length} in view</InfoChip>
          <InfoChip>{scheduledTodos.length} scheduled</InfoChip>
          <InfoChip>{openCount} open</InfoChip>
          <InfoChip tone="muted">{completedCount} done</InfoChip>
        </BoardBadges>
      </BoardHeader>

      <div style={toolbarStyle}>
        <div style={toolbarGroupStyle}>
          {SCOPE_OPTIONS.map((option) => (
            <Button
              key={option}
              onClick={() => updateView(option, anchor)}
              size="subtle"
              type="button"
              variant={scope === option ? "primary" : "ghost"}
            >
              {option[0].toUpperCase()}
              {option.slice(1)}
            </Button>
          ))}
        </div>

        <div style={toolbarGroupStyle}>
          <Button onClick={() => updateView(scope, moveAnchor(anchor, scope, -1))} size="subtle" type="button" variant="ghost">
            Previous
          </Button>
          <Button onClick={() => updateView(scope, today)} size="subtle" type="button" variant="ghost">
            Today
          </Button>
          <Button onClick={() => updateView(scope, moveAnchor(anchor, scope, 1))} size="subtle" type="button" variant="ghost">
            Next
          </Button>
        </div>
      </div>

      <div style={{ ...toolbarGroupStyle, marginTop: "0.85rem", justifyContent: "space-between" }}>
        <div>
          <strong>{formatPeriodLabel(anchor, scope)}</strong>
          <CopyText as="div">
            {tasksInScope.length === 0
              ? `No tasks are due in this ${scope}.`
              : `${tasksInScope.length} task${tasksInScope.length === 1 ? "" : "s"} due in this ${scope}.`}
          </CopyText>
        </div>

        <CopyText as="div">
          {unscheduledCount === 0
            ? "Every task currently has a due date."
            : `${unscheduledCount} task${unscheduledCount === 1 ? "" : "s"} still have no due date.`}
        </CopyText>
      </div>

      {scope === "day" ? renderDayView() : scope === "week" ? renderWeekView() : scope === "month" ? renderMonthView() : renderYearView()}
    </BoardPanel>
  );
}
